/**
 * Color Swatch AJAX - Cambio dinámico de producto por color
 *
 * Intercepta clicks en los swatches de color (variant-button-metafield)
 * y carga el nuevo producto sin recargar la página.
 *
 * Pide solo la sección del PDP (?section_id=<id de la sección actual>). Ese id
 * incluye la plantilla, así que solo responde si el producto nuevo usa la misma
 * plantilla; si no (404), pide la página completa, que respeta el template
 * asignado a cada producto con su configuración de bloques (guía de tallas, etc.).
 *
 * Funciona con el patrón de agrupación de productos por color
 * donde cada color es un producto Shopify independiente vinculado
 * mediante el metafield product.metafields.custom.variantes.
 *
 * ─── FLUJO PRINCIPAL ────────────────────────────────────────────────────────
 *
 *  1. init()
 *     └─ Escucha clicks globales → handleSwatchClick()
 *     └─ initializeOnPageLoad() → ajusta el DOM en la carga inicial
 *
 *  2. Click en swatch → handleSwatchClick()
 *     └─ Extrae handle del nuevo producto
 *     └─ loadProduct(handle, container)
 *          ├─ Captura orden actual de swatches
 *          ├─ fetch('<root_url>/products/<handle>?section_id=…')  ← root_url = prefijo de idioma
 *          │    (o la página completa si la sección no está en su plantilla)
 *          ├─ Parsea                                    → cede el hilo
 *          ├─ swapProduct(): evento hs:pdp-before-swap + onUnload de la
 *          │    instancia anterior, mueve los nodos nuevos al container,
 *          │    reinicializa, restaura swatches, Yotpo, URL  → cede el hilo
 *          └─ finishSwap(): AOS, lazySizes, popups, Completa tu look
 *
 * ─── CICLO DE VIDA DE LOS SCRIPTS INLINE DEL PDP ────────────────────────────
 *
 *  Los <script> del contenido nuevo se vuelven a ejecutar en cada swap. Los
 *  que registran listeners en document/window o timers deben soltarlos al
 *  recibir `hs:pdp-before-swap` (ver envia-edd-pdp, tier-banner-pdp,
 *  size-recommender, variant-button-metafield); si no, se suman en cada cambio
 *  de color.
 *
 * ─── ESTADO GLOBAL (módulo IIFE) ────────────────────────────────────────────
 *
 *  isLoading        — Semáforo que evita cargas simultáneas
 *  loadSeq          — Id de la carga más reciente (descarta etapas diferidas viejas)
 *  persistentTrigger — Nodo DOM del botón "Guía de tallas" creado por JS
 *                      Se mantiene entre swaps para no perder el evento click
 *  sizeChartContent  — HTML del contenido de la guía de tallas extraído del
 *                      bloque @app del template; se reutiliza en cada swap
 */

(function () {
  'use strict';

  // ── Selectores CSS centralizados para fácil mantenimiento ──────────────────
  const SELECTORS = {
    sectionContainer: '[data-section-type="product"]',   // Wrapper del PDP completo
    colorSwatchLink:  '.color-fieldset .color-swatch a', // Links de cada swatch de color
    productPhotos:    '[data-product-photos]',            // Slider de imágenes (Flickity)
    // data-is-size="true" está solo en el fieldset de tallas (variant-button.liquid línea 44).
    // El fieldset de color también tiene clase .sizes-fieldset cuando se renderiza con
    // variant-button.liquid (productos de un solo color), por lo que sin este atributo
    // querySelector('.sizes-fieldset') encontraría el fieldset de color primero.
    sizesFieldset:    '.sizes-fieldset[data-is-size="true"]'
  };

  // SVG del ícono de guía de tallas; inline para evitar requests adicionales
  const SIZE_CHART_ICON = '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-size-chart" viewBox="0 0 64 64"><defs><style>.a{fill:none;stroke:#000;stroke-width:2px}</style></defs><path class="a" d="M22.39 33.53c-7.46 0-13.5-3.9-13.5-8.72s6-8.72 13.5-8.72 13.5 3.9 13.5 8.72a12 12 0 0 1-.22 1.73"/><ellipse cx="22.39" cy="24.81" rx="3.28" ry="2.12"/><path class="a" d="M8.89 24.81V38.5c0 7.9 6.4 9.41 14.3 9.41h31.92V33.53H22.39M46.78 33.53v7.44M38.65 33.53v7.44M30.52 33.53v7.44M22.39 33.53v7.44"/></svg>';

  // Estado del módulo — persiste entre swaps de color
  let isLoading = false;         // true desde el click hasta que el producto nuevo queda inicializado
  let loadSeq = 0;               // Identifica la carga más reciente; las etapas diferidas de una vieja no corren
  let persistentTrigger = null;  // El <span> "Guía de tallas" creado una sola vez
  let sizeChartContent = null;   // HTML del popup de la guía (extraído del bloque Shopify)
  let yotpoObserver = null;      // Un solo observer esperando al widget de Yotpo (antes se sumaba uno por swap)

  // Si el fetch no responde en este tiempo se aborta y se navega a la URL del
  // producto: antes el PDP podía quedar semitransparente y sin clics para siempre.
  const FETCH_TIMEOUT_MS = 15000;

  // ── Punto de entrada ───────────────────────────────────────────────────────

  function init() {
    // Delegación de eventos: un solo listener en document captura todos los clicks.
    // Evita tener que re-bindear tras cada swap de innerHTML.
    document.addEventListener('click', handleSwatchClick);

    // Ajustar el DOM en la carga inicial (posición de size chart, medidas, Yotpo)
    initializeOnPageLoad();
  }

  /**
   * Corre una sola vez al cargar la página para colocar elementos en su
   * posición correcta. El servidor de Shopify renderiza los bloques en el
   * orden del block_order del template JSON, que no siempre coincide con
   * el orden visual deseado.
   *
   * Problemas que resuelve:
   *  - La guía de tallas (.size-chart__standalone) aparece arriba del botón ATC
   *    → se mueve junto al label de tallas mediante attachPersistentSizeChart()
   *  - El contenedor de medidas (#product-sizes-info) está oculto por CSS
   *    → se "clickea" la talla activa para disparar changeVariantSizes()
   *  - El widget de estrellas Yotpo aparece sobre el precio
   *    → se mueve a #start_bar_container mediante repositionYotpoWidget()
   */
  function initializeOnPageLoad() {
    const container = document.querySelector(SELECTORS.sectionContainer);
    if (!container) return;

    if (container.querySelector(SELECTORS.sizesFieldset)) {
      // Mostrar las medidas de la talla pre-seleccionada por el servidor
      triggerSelectedSize(container);

      // Mover el bloque de guía de tallas junto al label (si el producto lo tiene)
      if (container.querySelector('.size-chart__standalone')) {
        initPersistentSizeChart(container);
        if (persistentTrigger) {
          removeSizeChartElements(container); // Eliminar el bloque en posición incorrecta
          attachPersistentSizeChart(container); // Reinsertar junto al label de tallas
        }
      }
    }

    // Mover el widget de reseñas Yotpo al lado del precio
    repositionYotpoWidget(container);

    // Red de seguridad: si por algún caso borde el trigger de guía de tallas no se
    // llegó a insertar (p.ej. sin fieldset de tallas), no dejar el skeleton animando
    // indefinidamente.
    setTimeout(function () {
      var skels = document.querySelectorAll('[data-size-chart-skel]');
      for (var i = 0; i < skels.length; i++) skels[i].remove();
    }, 4000);
  }

  // ── Yotpo Reviews ─────────────────────────────────────────────────────────

  /**
   * Mueve el widget de estrellas Yotpo (.yotpo-reviews-star-ratings-widget)
   * al #start_bar_container para que quede inline junto al precio.
   *
   * El SDK de Yotpo carga de forma asíncrona e inyecta el widget dentro del
   * bloque @app que el servidor renderizó ANTES del bloque de precio, por lo
   * que en la carga inicial las estrellas aparecen encima del precio en lugar
   * de al lado. Esta función observa el DOM y mueve el nodo real (no una copia)
   * al contenedor correcto en cuanto Yotpo lo inyecte.
   *
   * Se usa appendChild (mover nodo real) en lugar de clonar+eliminar, para
   * evitar que el SDK detecte una eliminación y re-renderice el widget.
   *
   * @param {Element} container - El section container del PDP
   */
  function repositionYotpoWidget(container) {
    var starBarContainer = container.querySelector('#start_bar_container');
    if (!starBarContainer) return; // Este producto no tiene el layout de precio+estrellas

    // Caso A: El SDK ya inyectó el widget antes de que esta función corriera
    var existing = document.querySelector('.yotpo-reviews-star-ratings-widget');
    if (existing) {
      if (!starBarContainer.contains(existing)) {
        starBarContainer.appendChild(existing);
        attachYotpoScrollListener();
      }
      return;
    }

    // Caso B: El SDK todavía no ha inyectado el widget — esperar con MutationObserver.
    // Se desconecta inmediatamente al detectar el widget para evitar loops.
    // El setTimeout(0) difiere la operación un tick, asegurando que cualquier
    // otro observer (como el de theme.js) que clone/elimine el widget termine
    // primero, y entonces movemos el nodo final al lugar correcto.
    // Solo puede haber uno: observa todo el body y hace un querySelector en cada
    // lote de mutaciones, y antes cada swap sin widget sumaba otro que nunca se
    // desconectaba.
    disconnectYotpoObserver();
    yotpoObserver = new MutationObserver(function () {
      var widget = document.querySelector('.yotpo-reviews-star-ratings-widget');
      if (!widget) return;

      disconnectYotpoObserver();

      setTimeout(function () {
        var target = document.querySelector('#start_bar_container');
        if (!target) return;

        var w = document.querySelector('.yotpo-reviews-star-ratings-widget');
        if (!w) return;

        if (!target.contains(w)) {
          target.appendChild(w);
          attachYotpoScrollListener();
        }
      }, 0);
    });

    yotpoObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function disconnectYotpoObserver() {
    if (yotpoObserver) {
      yotpoObserver.disconnect();
      yotpoObserver = null;
    }
  }

  /**
   * Agrega un listener de click al widget de estrellas Yotpo para que,
   * al hacer click, haga scroll suave hasta la sección de reseñas completas
   * que normalmente está más abajo en el PDP.
   */
  function attachYotpoScrollListener() {
    var widget = document.querySelector('.yotpo-reviews-star-ratings-widget');
    if (!widget) return;

    // El nodo del widget se rescata y reinserta en cada swap: enlazarlo una vez.
    if (widget.__hsScrollBound) return;
    widget.__hsScrollBound = true;

    widget.addEventListener('click', function () {
      var reviewsSection = document.querySelector('.yotpo-main-widget-layout.yotpo-align-text');
      if (reviewsSection) {
        reviewsSection.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }

  // ── Manejo de clicks en swatches ───────────────────────────────────────────

  /**
   * Handler de clicks delegado desde document.
   * Solo actúa si el click fue en un link de swatch de color y si el
   * producto destino es diferente al producto actualmente cargado.
   *
   * @param {MouseEvent} e
   */
  function handleSwatchClick(e) {
    const link = e.target.closest(SELECTORS.colorSwatchLink);
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href) return;

    const container = link.closest(SELECTORS.sectionContainer);
    if (!container) return;

    const currentHandle = container.getAttribute('data-product-handle');
    const newHandle = extractHandleFromUrl(href);

    // Ignorar clicks en el color actualmente activo
    if (!newHandle || newHandle === currentHandle) {
      e.preventDefault();
      return;
    }

    e.preventDefault();
    e.stopPropagation(); // Evitar que Shopify o Impulse también procesen el click

    if (isLoading) return; // Ignorar clicks mientras hay una carga en curso

    loadProduct(newHandle, container);
  }

  /**
   * Extrae el handle de producto de una URL de Shopify.
   * Ejemplo: '/products/vestido-rojo?color=rojo' → 'vestido-rojo'
   * Funciona igual con el prefijo de idioma: '/en/products/vestido-rojo' → 'vestido-rojo'
   *
   * @param {string} url
   * @returns {string|null}
   */
  function extractHandleFromUrl(url) {
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  // ── Rutas localizadas ──────────────────────────────────────────────────────

  /**
   * Prefijo de idioma del storefront. routes.root_url llega como
   * theme.routes.home ('/' en el idioma primario, '/en' en los demás);
   * window.Shopify.routes.root es el respaldo que inyecta Shopify.
   *
   * Sin este prefijo, el fetch del swap pedía siempre '/products/<handle>',
   * que Shopify sirve en el idioma primario: en /en el PDP volvía en español
   * al cambiar de color, y el history.replaceState dejaba al comprador fuera
   * de la versión en inglés.
   *
   * @returns {string} '' en el idioma primario, '/en' (etc.) en los demás
   */
  function localeRoot() {
    var root = (window.theme && window.theme.routes && window.theme.routes.home)
      || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      || '/';
    return String(root).replace(/\/+$/, '');
  }

  /**
   * URL localizada de un producto.
   *
   * @param {string} handle
   * @returns {string} p.ej. '/en/products/vestido-rojo'
   */
  function productUrl(handle) {
    return localeRoot() + '/products/' + handle;
  }

  // ── Carga AJAX del producto ────────────────────────────────────────────────

  /**
   * Cede el hilo principal para que un toque pendiente se procese y se pinte
   * antes de seguir. scheduler.yield() donde existe; si no, setTimeout(0).
   */
  function yieldToMain() {
    if (window.scheduler && typeof window.scheduler.yield === 'function') {
      return window.scheduler.yield();
    }
    return new Promise(function (resolve) { setTimeout(resolve, 0); });
  }

  function fetchText(url, signal) {
    return fetch(url, { signal: signal }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.text();
    });
  }

  /**
   * Parsea el HTML recibido y devuelve el section container del producto.
   * Lanza si no viene: así nunca se toca el DOM con una respuesta incompleta.
   */
  function parseProductContainer(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const container = doc.querySelector(SELECTORS.sectionContainer);
    if (!container) throw new Error('Section container not found');
    return container;
  }

  /**
   * Pide solo la sección del PDP con la Section Rendering API (~0.3 MB) en
   * lugar de la página completa (~1.2 MB). El container que devuelve es
   * idéntico al de la página completa, porque el section id incluye la
   * plantilla (template--<id>__main): si el producto nuevo usa otra plantilla
   * (preventa, etc.) ese id no existe para él, Shopify responde 404 y se pide
   * la página completa, que respeta su plantilla y sus bloques como antes.
   *
   * @returns {Promise<Element>} el container del producto nuevo (sin conectar)
   */
  function fetchProductContainer(localizedUrl, sectionId, signal) {
    const fullPage = function () {
      return fetchText(localizedUrl, signal).then(parseProductContainer);
    };
    if (!sectionId) return fullPage();

    return fetchText(localizedUrl + '?section_id=' + encodeURIComponent(sectionId), signal)
      .then(parseProductContainer)
      .catch(function (error) {
        if (signal.aborted) throw error;
        return fullPage();
      });
  }

  /**
   * Carga un producto nuevo vía fetch y reemplaza el contenido del PDP.
   *
   * Secuencia de operaciones:
   *  1. Captura el orden actual de swatches (antes de que el swap lo altere)
   *  2. Inicializa el trigger persistente de guía de tallas (si no existe)
   *  3. Aplica feedback visual de carga (opacity + pointer-events)
   *  4. Fetch de la sección del nuevo producto (o de la página completa)
   *  5. Parsea el HTML y extrae el section container       → cede el hilo
   *  6. swapProduct(): limpia lo anterior, reemplaza el contenido e inicializa
   *     el producto nuevo, todo en una sola tarea (precio, tallas, formulario,
   *     URL y overlay quedan consistentes antes del siguiente paint) → cede
   *  7. finishSwap(): AOS, lazySizes, popups, Completa tu look, reseñas
   *
   * En caso de error (o si el fetch tarda más de FETCH_TIMEOUT_MS) hace
   * fallback a navegación normal (window.location.href) para garantizar que
   * el usuario siempre llega al producto correcto.
   *
   * @param {string}  handle       - Handle Shopify del producto a cargar
   * @param {Element} oldContainer - El section container actualmente en el DOM
   */
  function loadProduct(handle, oldContainer) {
    isLoading = true;
    const seq = ++loadSeq;
    const sectionId = oldContainer.getAttribute('data-section-id');

    // Capturar el orden actual de handles antes del swap.
    // El nuevo HTML pondrá el color nuevo primero (por cómo Shopify ordena
    // el metafield custom.variantes), así que guardamos el orden original
    // para restaurarlo en restoreSwatchOrder() después del swap.
    const colorFieldset = oldContainer.querySelector('.color-fieldset');
    const orderedHandles = colorFieldset
      ? Array.from(colorFieldset.querySelectorAll('a[data-product-handle]'))
          .map(function (a) { return a.getAttribute('data-product-handle'); })
      : [];

    // Inicializar el trigger persistente ahora (antes del swap) porque
    // initPersistentSizeChart() lee el DOM del producto actual para
    // extraer el HTML de la guía de tallas.
    initPersistentSizeChart(oldContainer);

    // No se desancla persistentTrigger explícitamente del DOM antes del swap.
    // El reemplazo del contenido lo desconecta de forma natural junto
    // con todo el contenido, pero la referencia JS sobrevive para ser
    // reinsertada en attachPersistentSizeChart(). Eliminarlo antes causaría
    // un layout shift visible en el .variant__label (flex con un hijo menos).

    // Feedback visual de carga
    oldContainer.style.opacity = '0.5';
    oldContainer.style.pointerEvents = 'none';
    oldContainer.style.transition = 'opacity 0.2s ease';

    // Bloquear la altura del container durante todo el swap para evitar CLS externo.
    // Los elementos debajo del PDP no se moverán mientras la sección mantenga su altura.
    oldContainer.style.minHeight = oldContainer.offsetHeight + 'px';

    // Se pide la URL localizada (localeRoot + handle) y no el href del <a>, para
    // que el swap respete el idioma actual aunque el link venga sin prefijo.
    const localizedUrl = productUrl(handle);

    const controller = new AbortController();
    const timeoutId = setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS);

    fetchProductContainer(localizedUrl, sectionId, controller.signal)
      .then(function (newContainer) {
        clearTimeout(timeoutId);
        // El parseo ya fue una tarea propia: ceder antes del swap. El PDP sigue
        // con pointer-events:none, así que nada puede cambiar en medio.
        return yieldToMain().then(function () { return newContainer; });
      })
      .then(function (newContainer) {
        swapProduct(oldContainer, newContainer, sectionId, localizedUrl, orderedHandles);
        isLoading = false;
        return yieldToMain();
      })
      .then(function () {
        // Si mientras tanto empezó otro cambio de color, sus propias etapas se
        // encargan; no tocar un DOM que ya no corresponde a esta carga.
        if (seq !== loadSeq || !oldContainer.isConnected) return;
        finishSwap(oldContainer);
      })
      .catch(function (error) {
        // Fallback: si algo falla, navegar normalmente para que el usuario
        // llegue al producto correcto aunque recargue la página
        clearTimeout(timeoutId);
        console.error('[ColorSwatchAjax] Error:', error);
        oldContainer.style.minHeight = '';
        isLoading = false;
        window.location.href = localizedUrl;
      });
  }

  /**
   * El swap en sí. Corre en una sola tarea a propósito: entre el reemplazo del
   * contenido y la inicialización de theme.Product no debe haber un paint ni un
   * toque, o se vería (y se podría enviar al carrito) un formulario sin enlazar.
   *
   * Orden: 1) limpiar los recursos del producto anterior, 2) reemplazar el
   * contenido, 3) inicializar los componentes nuevos una sola vez.
   */
  function swapProduct(oldContainer, newContainer, sectionId, localizedUrl, orderedHandles) {
    // Extraer el precio para Kueski del data-attribute del nuevo container.
    // Antes se buscaba `var precio = N;` en un <script> inline, pero esa
    // lógica se movió a assets/product-template.js y el precio ahora viaja
    // como data-product-price en el contenedor data-section-type="product".
    var kueskiPrecio = parseInt(newContainer.getAttribute('data-product-price'), 10);
    if (isNaN(kueskiPrecio)) kueskiPrecio = null;

    // Capturar la altura del viewport de Flickity antes de destruirlo.
    // Después del swap la usaremos para evitar que el slideshow
    // colapse mientras Flickity se reinicializa (causa CLS interno).
    const flickityViewport = oldContainer.querySelector('.flickity-viewport');
    const storedSlideHeight = flickityViewport ? flickityViewport.offsetHeight : 0;

    // ── 1. Limpiar ──────────────────────────────────────────────────────────

    // Los scripts inline del PDP (EDD, banner de descuentos, recomendador de
    // talla...) cuelgan listeners de document y timers; con este evento los
    // sueltan antes de que sus nodos desaparezcan. Si el producto nuevo trae el
    // mismo componente, su script se vuelve a ejecutar más abajo y se registra
    // una sola vez.
    document.dispatchEvent(new CustomEvent('hs:pdp-before-swap'));

    unloadProductSection(oldContainer, sectionId);
    disconnectYotpoObserver();

    // Copiar data-attributes del nuevo producto al container existente
    // (el container del DOM mantiene el mismo nodo, cambian sus atributos)
    transferAttributes(newContainer, oldContainer);

    // Preservar el widget de Yotpo antes del swap: las reviews son compartidas
    // entre todos los colores del mismo producto, así que el nodo existente
    // (ya hidratado por el SDK) se puede reutilizar directamente en lugar de
    // pedir al SDK que re-inicialice el widget para el nuevo product-id.
    const savedYotpoWidget = document.querySelector('.yotpo-reviews-star-ratings-widget');
    if (savedYotpoWidget?.parentNode) {
      savedYotpoWidget.parentNode.removeChild(savedYotpoWidget);
    }

    // ── 2. Reemplazar ───────────────────────────────────────────────────────

    // Se mueven los nodos ya parseados en lugar de `innerHTML = newContainer.innerHTML`,
    // que serializaba ~300 KB de HTML y lo volvía a parsear. Los <noscript> se
    // descartan: DOMParser parsea su contenido como elementos reales y sus <img>
    // se descargarían al insertarlos (con innerHTML quedaban como texto inerte).
    newContainer.querySelectorAll('noscript').forEach(function (el) { el.remove(); });
    const fragment = document.createDocumentFragment();
    while (newContainer.firstChild) fragment.appendChild(newContainer.firstChild);
    oldContainer.textContent = '';
    oldContainer.appendChild(fragment);

    // Bloquear la altura del nuevo slideshow inmediatamente después del swap.
    // Antes de que Flickity se reinicialice, las slides se apilan verticalmente
    // y la columna de imágenes se hace muy alta, desplazando el contenido.
    if (storedSlideHeight > 0) {
      const newPhotosEl = oldContainer.querySelector(SELECTORS.productPhotos);
      if (newPhotosEl) {
        newPhotosEl.style.minHeight = storedSlideHeight + 'px';
        newPhotosEl.style.overflow = 'hidden';
      }
    }

    // DOMParser normaliza el HTML y puede alterar la estructura interna
    // de los labels de talla que necesitan una <table> para su estilo.
    // Esta función la restaura si fue limpiada.
    restoreSizeLabelTables(oldContainer);

    // Re-ejecutar scripts inline que el nuevo HTML pueda traer
    // (datos de variantes, configuración de sección, etc.)
    executeInlineScripts(oldContainer);

    // Reposicionar la guía de tallas persistente junto al label de tallas
    removeSizeChartElements(oldContainer); // Eliminar bloque en posición incorrecta
    attachPersistentSizeChart(oldContainer); // Reinsertar en posición correcta

    // ── 3. Inicializar ──────────────────────────────────────────────────────

    // Reinicializar los componentes del tema que definen lo que se compra
    // (sección Impulse: variantes, precio, botón, galería)
    reinitialize(oldContainer);

    // Aplicar el precio de Kueski extraído antes del swap.
    // Se llama aquí (no dentro de reinitialize) porque kueskiPrecio es una
    // variable local de swapProduct() y no estaría en scope dentro
    // de reinitialize().
    reinitKueskiPrice(oldContainer, kueskiPrecio);

    // Restaurar el orden visual de los swatches al orden que tenía antes del swap
    restoreSwatchOrder(oldContainer, orderedHandles);

    // Notificar el swap de producto al widget de reseñas propio (que vive FUERA
    // del contenedor swapeado y por eso no se re-renderiza solo). Escucha este
    // evento para actualizar el GID y re-pedir su lista para el nuevo producto.
    // También lo usan pdp-v2.js (línea de dinero) y wishlist-hearts.js (corazón),
    // que pintan nodos del contenido nuevo: por eso va en esta misma tarea.
    // No toca la lógica de Yotpo de abajo; solo emite el evento.
    try {
      var swappedProductId = oldContainer.getAttribute('data-product-id');
      if (swappedProductId) {
        document.dispatchEvent(new CustomEvent('hs:product-swapped', {
          detail: {
            productId: swappedProductId,
            gid: 'gid://shopify/Product/' + swappedProductId
          }
        }));
      }
    } catch (e) {
      // No romper el swap si el evento falla; el widget de reseñas es opcional.
    }

    // Reinsertar el widget de Yotpo preservado junto al precio.
    // Si se pudo rescatar el nodo antes del swap, se inserta directamente
    // (reutilizando el widget ya hidratado). Si no existía (producto sin
    // reviews), se usa repositionYotpoWidget para esperar al SDK via observer.
    const starBar = oldContainer.querySelector('#start_bar_container');
    if (savedYotpoWidget && starBar) {
      starBar.appendChild(savedYotpoWidget);
      attachYotpoScrollListener();
    } else {
      repositionYotpoWidget(oldContainer);
    }

    // Actualizar URL y título de la pestaña sin recargar la página. Va en esta
    // misma tarea, después de inicializar las variantes: así el replaceState
    // diferido de theme.Variants (que lleva la ruta del producto anterior) se
    // descarta solo. Se conservan los parámetros de campaña; ?variant= no,
    // porque era del producto anterior.
    history.replaceState({}, '', localizedUrl + carriedQuery() + window.location.hash);
    const newTitle = oldContainer.getAttribute('data-product-title');
    if (newTitle) {
      document.title = newTitle + document.title.substring(document.title.indexOf(' –'));
    }

    // Quitar overlay de carga: el producto nuevo ya está listo para interactuar
    oldContainer.style.opacity = '';
    oldContainer.style.pointerEvents = '';
    oldContainer.style.transition = '';

    // Liberar los bloqueos de altura después de que Flickity haya tenido
    // tiempo de reinicializarse (~400 ms). Antes de liberarlos, Flickity ya
    // habrá fijado su propio alto mediante .flickity-viewport, por lo que
    // quitar el min-height no provoca ningún salto visible.
    setTimeout(function () {
      var photosEl = oldContainer.querySelector(SELECTORS.productPhotos);
      if (photosEl) {
        photosEl.style.minHeight = '';
        photosEl.style.overflow = '';
      }
      oldContainer.style.minHeight = '';
    }, 400);
  }

  /**
   * Query string actual sin ?variant= (la variante era del producto anterior).
   *
   * @returns {string} '' o '?utm_source=...'
   */
  function carriedQuery() {
    const params = new URLSearchParams(window.location.search);
    params.delete('variant');
    const query = params.toString();
    return query ? '?' + query : '';
  }

  /**
   * Da de baja la instancia de theme.Product del producto anterior.
   *
   * onUnload() quita sus listeners (variantChange en el container, que sobrevive
   * al swap, y shopify_xr_launch en document) y destruye su slideshow junto con
   * el listener de resize. Antes solo se sacaba del registry y esos listeners
   * se acumulaban: los handlers de la instancia vieja seguían corriendo en cada
   * cambio de talla.
   */
  function unloadProductSection(container, sectionId) {
    const instance = window.theme && theme.sections ? theme.sections._removeInstance(sectionId) : null;
    if (instance && typeof instance.onUnload === 'function') {
      try {
        instance.onUnload();
      } catch (e) {
        /* no bloquear el swap si la instancia ya estaba a medio destruir */
      }
    }

    // Respaldo: si no había instancia (o su onUnload falló), destruir Flickity
    // directamente para que no quede apuntando al DOM viejo
    destroyFlickity(container);

    // Limpiar event listeners con namespace del tema para este section
    cleanupListeners(container, sectionId);
  }

  /**
   * Reordena los swatches de color para que mantengan el orden
   * que tenían antes del swap AJAX.
   *
   * El metafield custom.variantes de cada producto pone su propio
   * color primero, por lo que tras cargar un producto nuevo el orden
   * cambia. Esta función toma el orden capturado del DOM anterior
   * y mueve los .variant-input dentro del .color-fieldset para
   * replicarlo exactamente.
   *
   * Ejemplo: si antes del swap el orden era [rojo, azul, verde] y
   * el nuevo producto (azul) lo renderizó como [azul, rojo, verde],
   * esta función reordena el DOM para que quede [rojo, azul, verde].
   *
   * @param {Element}  container       - El section container actualizado.
   * @param {string[]} orderedHandles  - Array de handles en el orden original.
   */
  function restoreSwatchOrder(container, orderedHandles) {
    if (!orderedHandles || orderedHandles.length === 0) return;

    const colorFieldset = container.querySelector('.color-fieldset');
    if (!colorFieldset) return;

    // Construir mapa handle → nodo .variant-input para búsqueda O(1)
    const handleToWrapper = {};
    colorFieldset.querySelectorAll('a[data-product-handle]').forEach(function (a) {
      const h = a.getAttribute('data-product-handle');
      const wrapper = a.closest('.variant-input');
      if (h && wrapper) handleToWrapper[h] = wrapper;
    });

    // appendChild al final del fieldset en el orden original.
    // Como todos los nodos ya están en el DOM, esto solo los reordena
    // sin crear copias ni disparar renders adicionales.
    orderedHandles.forEach(function (h) {
      const wrapper = handleToWrapper[h];
      if (wrapper) colorFieldset.appendChild(wrapper);
    });
  }

  // ── Guía de tallas persistente ─────────────────────────────────────────────

  /**
   * Crea una sola vez el trigger persistente de guía de tallas y extrae
   * el HTML del popup del bloque @app del template actual.
   *
   * Se llama "persistente" porque este nodo JS sobrevive entre swaps:
   * antes de cada swap se desancla del DOM, y después del swap se
   * reinserta. Así el event listener del click no se pierde.
   *
   * No hace nada si ya fue creado (guard: if persistentTrigger) para
   * garantizar que solo existe una instancia aunque se llame múltiples veces.
   *
   * @param {Element} container - Section container del que extraer el contenido
   */
  function initPersistentSizeChart(container) {
    if (persistentTrigger) return; // Ya inicializado, no crear duplicados

    // Buscar el contenido HTML de la guía dentro del tool-tip-trigger del bloque @app.
    // El bloque puede aparecer en distintas posiciones del DOM según el template,
    // así que se busca por data-tool-tip="size-chart" en lugar de por posición.
    const sources = container.querySelectorAll('tool-tip-trigger[data-tool-tip="size-chart"]');
    for (let i = 0; i < sources.length; i++) {
      const contentEl = sources[i].querySelector('[data-tool-tip-content]');
      if (contentEl && contentEl.innerHTML.trim()) {
        sizeChartContent = contentEl.innerHTML;
        break;
      }
    }

    if (!sizeChartContent) return; // Este producto no tiene guía de tallas

    // Crear el botón trigger
    persistentTrigger = document.createElement('span');
    persistentTrigger.className = 'size-chart-trigger';
    persistentTrigger.setAttribute('role', 'button');
    persistentTrigger.setAttribute('tabindex', '0');
    persistentTrigger.innerHTML = SIZE_CHART_ICON + ' '
      + ((window.theme && window.theme.strings && window.theme.strings.sizeChartTrigger) || 'Guía de tallas');

    // Abrir el popup de guía de tallas disparando el CustomEvent que
    // escucha el componente ToolTip de Impulse (theme.js)
    persistentTrigger.addEventListener('click', function (e) {
      e.stopPropagation();
      document.dispatchEvent(new CustomEvent('tooltip:open', {
        detail: {
          context: 'size-chart',
          content: sizeChartContent
        },
        bubbles: true
      }));
      // Asegurarse de que el elemento <tool-tip> sea visible (el ToolTip
      // de Impulse usa display:none por CSS; tooltip:open solo actualiza
      // el contenido pero no siempre cambia el display)
      const toolTipEl = document.querySelector('tool-tip');
      if (toolTipEl) {
        toolTipEl.style.display = 'flex';
      }
      var _p = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'size_chart_opened',
        product_id: _p ? String(_p.id) : null,
        product_title: _p ? _p.title : null
      });
    });

    // Accesibilidad: permitir activar con Enter y Espacio (como un botón real)
    persistentTrigger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        persistentTrigger.click();
      }
    });
  }

  /**
   * Elimina del container todos los elementos relacionados con la guía de
   * tallas que el servidor renderizó: el bloque standalone (.size-chart__standalone)
   * y cualquier tool-tip-trigger inline dentro de los labels de talla.
   *
   * Se hace para limpiar el DOM antes de insertar el persistentTrigger
   * en la posición correcta (junto al label de tallas).
   *
   * @param {Element} container
   */
  function removeSizeChartElements(container) {
    // Eliminar el bloque completo de guía de tallas (posición incorrecta)
    const standalone = container.querySelector('.size-chart__standalone');
    if (standalone) {
      // El bloque puede estar envuelto en un .product-block; eliminar el wrapper
      const parentBlock = standalone.closest('.product-block');
      if (parentBlock) parentBlock.remove();
      else standalone.remove();
    }

    // Eliminar triggers inline que aparecen dentro de .variant__label-info
    // para no tener dos disparadores del mismo popup
    const inlineTriggers = container.querySelectorAll('tool-tip-trigger[data-tool-tip="size-chart"]');
    inlineTriggers.forEach(function (trigger) {
      const wrapper = trigger.closest('.variant__label-info');
      if (wrapper) wrapper.remove();
      else trigger.remove();
    });
  }

  /**
   * Inserta el persistentTrigger dentro del .variant__label del fieldset
   * de tallas, usando flexbox para alinearlo horizontalmente con el texto
   * del label ("Talla:").
   *
   * Resultado visual: [ Talla:  [ícono] Guía de tallas ]
   *
   * @param {Element} container
   */
  function attachPersistentSizeChart(container) {
    if (!persistentTrigger) return;

    const sizesFieldset = container.querySelector(SELECTORS.sizesFieldset);
    if (!sizesFieldset) return;

    // El .variant__label está dentro del .variant-wrapper que envuelve al fieldset
    const variantWrapper = sizesFieldset.closest('.variant-wrapper');
    const tallaLabel = variantWrapper?.querySelector('.variant__label');
    if (!tallaLabel) return;

    // Aplicar flex al label para que el trigger quede a la derecha del texto
    tallaLabel.style.display = 'flex';
    tallaLabel.style.justifyContent = 'space-between';
    tallaLabel.style.alignItems = 'center';

    tallaLabel.appendChild(persistentTrigger);

    // Quitar el skeleton del trigger (data-size-chart-skel, server-rendered en
    // variant-button.liquid) ahora que el trigger real ya está en su lugar. Se hace
    // en el mismo tick que el appendChild, así la transición skeleton → trigger es
    // instantánea y sin salto.
    var skel = tallaLabel.querySelector('[data-size-chart-skel]');
    if (skel) skel.remove();
  }

  // ── Utilidades de cleanup y reinicialización ───────────────────────────────

  /**
   * Destruye la instancia de Flickity del slider de imágenes antes del swap.
   * Si no se destruye, Flickity mantiene referencias al DOM antiguo y puede
   * causar errores o comportamientos incorrectos al inicializarse de nuevo.
   *
   * @param {Element} container
   */
  function destroyFlickity(container) {
    const slider = container.querySelector(SELECTORS.productPhotos);
    if (slider && typeof Flickity !== 'undefined') {
      const flkty = Flickity.data(slider);
      if (flkty) {
        try {
          flkty.destroy();
        } catch (e) {
          /* silenciar errores de destrucción */
        }
      }
    }
  }

  /**
   * Elimina event listeners con namespace del tema Impulse para este section.
   * Impulse registra listeners con namespace `.product-<sectionId>` en jQuery.
   * Si no se limpian antes del swap, pueden acumularse handlers duplicados.
   *
   * @param {Element} container
   * @param {string}  sectionId
   */
  function cleanupListeners(container, sectionId) {
    const namespace = '.product-' + sectionId;
    if (container.namespaces) {
      const keys = Object.keys(container.namespaces);
      for (let i = 0; i < keys.length; i++) {
        if (keys[i].indexOf(namespace) !== -1) {
          container.off(keys[i]);
        }
      }
    }
  }

  /**
   * Copia los data-attributes del producto nuevo al container existente en el DOM.
   * El container es el mismo nodo (no se reemplaza), solo cambia su contenido
   * (innerHTML) y sus atributos.
   *
   * También transfiere el `id` del container para que el tema Impulse pueda
   * encontrar la instancia de section por su ID después del swap.
   *
   * @param {Element} source - Container del HTML parseado (nuevo producto)
   * @param {Element} target - Container real en el DOM (producto actual)
   */
  function transferAttributes(source, target) {
    const attrs = [
      'data-product-id',
      'data-product-handle',
      'data-product-title',
      'data-product-url',
      'data-aspect-ratio',
      'data-img-url',
      'data-product-price'
    ];
    for (let i = 0; i < attrs.length; i++) {
      const val = source.getAttribute(attrs[i]);
      if (val !== null) target.setAttribute(attrs[i], val);
    }
    // El id del container puede cambiar entre productos (Shopify lo genera
    // con el section id que puede variar)
    const newId = source.getAttribute('id');
    if (newId) target.setAttribute('id', newId);
  }

  /**
   * Re-ejecuta los <script> inline presentes en el nuevo HTML.
   * Los <script> que crea DOMParser quedan marcados como no ejecutables (igual
   * que los de innerHTML), así que al moverlos al documento no corren. Se crean nodos script nuevos y se insertan
   * en el DOM para que el navegador los evalúe.
   *
   * Esto es necesario para que corran los scripts de configuración de
   * variantes, precios y datos del producto que Shopify inyecta inline.
   *
   * @param {Element} container
   */
  function executeInlineScripts(container) {
    const scripts = container.querySelectorAll('script');
    for (let i = 0; i < scripts.length; i++) {
      const oldScript = scripts[i];
      // Los bloques de datos (application/json, ld+json) no se ejecutan: el nodo
      // movido ya trae su contenido, no hace falta recrearlo.
      const type = (oldScript.getAttribute('type') || '').trim().toLowerCase();
      if (type && type !== 'text/javascript' && type !== 'application/javascript' && type !== 'module') continue;
      const newScript = document.createElement('script');
      if (oldScript.src) {
        newScript.src = oldScript.src; // Script externo: recargar por URL
      } else {
        newScript.textContent = oldScript.textContent; // Script inline: re-evaluar
      }
      // Copiar atributos (type, data-*, etc.) pero no src (ya manejado arriba)
      for (let j = 0; j < oldScript.attributes.length; j++) {
        const attr = oldScript.attributes[j];
        if (attr.name !== 'src') {
          newScript.setAttribute(attr.name, attr.value);
        }
      }
      oldScript.parentNode.replaceChild(newScript, oldScript);
    }
  }

  /**
   * Reinicializa, en la misma tarea del swap, los componentes de los que
   * depende lo que se ve y lo que se compra: variantes, precio, botón,
   * galería, acordeones, disponibilidad y medidas de la talla elegida.
   * Lo demás va en finishSwap(), después de ceder el hilo.
   *
   * @param {Element} container
   */
  function reinitialize(container) {
    // Registrar la nueva instancia de sección en el registry del tema Impulse.
    // Esto activa la lógica interna del ProductSection (sliders, variantes, etc.)
    if (window.theme && theme.sections && theme.sections.constructors['product']) {
      theme.sections._createInstance(
        container,
        theme.sections.constructors['product']
      );
    }

    // Re-bindear los acordeones (.collapsible-trigger) tras el swap.
    // Sin esto, los acordeones de "Descripción" y "Envíos y Devoluciones"
    // pierden su listener de click porque el innerHTML nuevo crea nodos DOM
    // frescos que nunca pasaron por theme.collapsibles.init().
    if (window.theme && theme.collapsibles && typeof theme.collapsibles.init === 'function') {
      theme.collapsibles.init(container);
    }

    // Re-verificar disponibilidad de variantes (stock, pickup en tienda, etc.)
    if (typeof window.checkAvailability === 'function') {
      window.checkAvailability();
    }

    // Ocultar swatches deshabilitados (agotados o no disponibles)
    container.querySelectorAll('.variant__input--color-swatch').forEach(function (color) {
      if (color.classList.contains('disabled')) {
        color.parentElement.style.display = 'none';
      }
    });

    // Mostrar las medidas de la talla pre-seleccionada
    triggerSelectedSize(container);
  }

  /**
   * Lo que puede esperar a que el producto nuevo ya esté pintado y usable.
   * Corre en otra tarea (ver loadProduct) para no alargar la del swap. Va
   * protegido: el producto ya está listo, y un fallo aquí no debe llegar al
   * .catch de loadProduct, que mandaría a la clienta a otra página.
   *
   * @param {Element} container
   */
  function finishSwap(container) {
    try {
      // Refrescar animaciones AOS (Animate On Scroll) para los nuevos elementos.
      // Los [data-aos] del PDP (galería y miniaturas) no tienen estilos que los
      // oculten antes de .aos-animate, así que esperar una tarea no se nota.
      if (window.AOS) {
        AOS.refreshHard();
      }

      // Disparar lazy loading en imágenes del nuevo contenido
      if (window.lazySizes) {
        lazySizes.autoSizer.checkElems();
      }

      // Re-bindear el popup de descuentos por niveles (si existe en el PDP)
      bindDiscountPopup(container);

      // Re-hidratar el widget "Completa tu look" con el DOM nuevo.
      // Su script se cargó una sola vez (guard window.__ctlLoaded), por lo que
      // el swap deja el widget sin bindear: en desktop se queda en
      // "Cargando…" y en mobile el pill no abre el drawer. boot() reinicializa
      // los nodos frescos (los ya inicializados se saltan por su guard __ctlInit).
      if (typeof window.__ctlBoot === 'function') {
        window.__ctlBoot();
      }
    } catch (e) {
      console.error('[ColorSwatchAjax] finishSwap:', e);
    }

    // Emitir scroll y resize para que componentes externos (sticky bars,
    // widgets de terceros) recalculen su posición con el nuevo contenido
    setTimeout(function () {
      window.dispatchEvent(new Event('scroll'));
      window.dispatchEvent(new Event('resize'));
    }, 300);
  }

  /**
   * Recalcula y aplica el precio de Kueski (producto / 4 quincenas) después
   * del swap de color.
   *
   * Problema: el script de product-template.liquid que calcula el precio
   * está envuelto en `document.addEventListener('DOMContentLoaded', ...)`.
   * Cuando executeInlineScripts() re-crea ese nodo script tras el swap,
   * el listener se registra pero DOMContentLoaded ya disparó, por lo que
   * el callback nunca corre y los spans `.kueski-price-value` y
   * `#kueski-accordion-price` quedan vacíos.
   *
   * Solución: buscar en los scripts inline del container el patrón
   * `var precio = NNNN;` que Liquid ya renderizó con el precio correcto,
   * extraer el valor y aplicarlo directamente sin depender del evento.
   *
   * @param {Element} container
   */
  function reinitKueskiPrice(container, precio) {
    // precio se extrae del doc completo en loadProduct() antes del swap,
    // porque el script que lo contiene (product-template.liquid líneas ~922+)
    // está fuera del div data-section-type="product" y no se incluye en el swap.
    if (!precio) return;

    var precioKueski = '$' + (precio / 100 / 4).toFixed(2);

    container.querySelectorAll('.kueski-price-value').forEach(function (el) {
      el.textContent = precioKueski;
    });

    var accordionPrice = container.querySelector('#kueski-accordion-price');
    if (accordionPrice) accordionPrice.textContent = precioKueski;
  }

  /**
   * Vincula los eventos de abrir/cerrar del popup de descuentos por nivel
   * que aparece en el PDP (#popupPDP). Se re-bindea tras cada swap porque
   * el innerHTML nuevo crea nodos DOM frescos sin listeners.
   *
   * @param {Element} container
   */
  function bindDiscountPopup(container) {
    const openBtn = container.querySelector('.openPopupBtnPDP');
    const closeBtn = container.querySelector('#closePopupBtnPDP');
    const popup = container.querySelector('#popupPDP');
    if (!openBtn || !closeBtn || !popup) return;

    openBtn.addEventListener('click', function () {
      popup.style.display = 'flex';
      document.body.classList.add('popup-open');
    });

    closeBtn.addEventListener('click', function () {
      popup.style.display = 'none';
      document.body.classList.remove('popup-open');
    });

    // Cerrar al hacer click fuera del contenido del popup (en el overlay)
    popup.addEventListener('click', function (e) {
      if (e.target === popup) {
        popup.style.display = 'none';
        document.body.classList.remove('popup-open');
      }
    });
  }

  /**
   * Restaura la estructura de <table> dentro de los labels de talla.
   *
   * Los labels de talla en el tema tienen esta estructura especial:
   *   <label><table><tr><td>S</td></tr></table></label>
   *
   * DOMParser (usado para parsear el HTML del fetch) puede limpiar o
   * normalizar esta estructura. Si lo hace, los estilos CSS que dependen
   * de la tabla no funcionan correctamente.
   *
   * Esta función detecta labels sin tabla y reconstruye la estructura.
   *
   * @param {Element} container
   */
  function restoreSizeLabelTables(container) {
    var labels = container.querySelectorAll(
      SELECTORS.sizesFieldset + ' label.variant__button-label'
    );

    labels.forEach(function (label) {
      if (label.querySelector('table')) return; // Ya tiene tabla, no tocar

      // Recopilar nodos de texto que contienen el nombre de la talla
      var textNodes = [];
      label.childNodes.forEach(function (node) {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '') {
          textNodes.push(node);
        }
      });

      if (textNodes.length === 0) return; // No hay texto que envolver

      var sizeText = textNodes.map(function (n) { return n.textContent; }).join('');
      textNodes.forEach(function (n) { label.removeChild(n); });

      // Reconstruir la estructura tabla requerida por el CSS del tema
      var table = document.createElement('table');
      table.setAttribute('style', 'border: 0px solid !important;border-spacing:0px !important');
      var tr = document.createElement('tr');
      var td = document.createElement('td');
      td.setAttribute('style', 'border: 0px solid !important;padding:0px;text-align: center;');
      td.setAttribute('translate', 'no'); // codigo de talla: no traducible
      td.textContent = sizeText.trim();
      tr.appendChild(td);
      table.appendChild(tr);
      label.appendChild(table);
    });
  }

  /**
   * "Simula" la selección de la talla activa para que se muestren sus medidas.
   *
   * El contenedor #product-sizes-info está oculto (display:none) por CSS.
   * Solo se vuelve visible cuando se llama a window.changeVariantSizes(),
   * que normalmente se dispara por el atributo onclick de cada label de talla.
   *
   * En la carga inicial y tras cada swap, nadie hace click, así que esta
   * función llama a changeVariantSizes() con la talla que ya viene marcada
   * como checked para que las medidas aparezcan inmediatamente.
   *
   * @param {Element} container
   */
  function triggerSelectedSize(container) {
    const selectedSize = container.querySelector(SELECTORS.sizesFieldset + ' input[type="radio"]:checked');
    if (!selectedSize) return;

    const label = container.querySelector('label[for="' + selectedSize.id + '"]');
    if (label && typeof window.changeVariantSizes === 'function') {
      const value = selectedSize.value;
      try {
        window.changeVariantSizes(label, encodeURIComponent(value).toUpperCase());
      } catch (e) {
        /* el producto podría no tener medidas definidas */
      }
    }
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────

  // Inicializar cuando el DOM esté listo.
  // El script se carga con `defer` en theme.liquid, así que normalmente
  // readyState ya es 'complete' o 'interactive', pero se maneja ambos casos.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
